import { handleJSAPIAccess, retryJSAPIOnInvalidSession } from './auth_access_util';

/**
 * 显示提示消息（替换 alert）
 * @param {string} message - 提示内容
 * @param {string} type - 消息类型：'success' | 'error' | 'info' | 'warning'
 * @param {number} duration - 显示时长（毫秒），默认1500
 */
export const showToast = async (message, type = 'info', duration = 1500) => {
    // 图标映射
    const iconMap = {
        'success': 'success',
        'error': 'error',
        'info': 'info',
        'warning': 'warn'
    };

    // 错误信息过长时（飞书原生 toast 会截断），自动降级为模态弹窗，确保用户能完整看到原因
    const useModal = type === 'error' && typeof message === 'string' && message.length > 35;
    if (useModal) {
        await showErrorModal(message);
        return;
    }

    // 使用统一的环境检测逻辑
    const realFeishuEnv = isRealFeishuEnv();
    
    // 尝试获取飞书SDK对象（依赖UserContext中已完成的鉴权）
    const sdkResult = getFeishuSDK();
    
    // 调试信息
    console.log('showToast - 环境检测:', realFeishuEnv);
    console.log('showToast - SDK:', sdkResult ? sdkResult.type : 'null');

    // 尝试调用飞书原生API
    if (realFeishuEnv && sdkResult) {
        const sdk = sdkResult.sdk;
        const sdkType = sdkResult.type;

        if (typeof sdk.showToast === 'function') {
            console.log(`调用 ${sdkType}.showToast:`, message, 'type:', type);
            sdk.showToast({
                title: message,
                icon: iconMap[type] || 'info',
                duration: duration,
                success: () => {
                    console.log('showToast success:', message);
                },
                fail: (err) => {
                    console.error(`${sdkType}.showToast fail:`, err);
                    // 如果showToast失败，使用 alert
                    alert(message);
                }
            });
            return;
        }
        
        console.warn('真实飞书环境但SDK.showToast不可用，降级使用原生alert');
    }
    
    // 非飞书环境或SDK不可用，降级使用原生 alert
    alert(message);
};

/**
 * 判断是否为真实飞书环境
 * @returns {boolean}
 */
const isRealFeishuEnv = () => {
    // 获取当前页面URL
    const location = window.location.href.toLowerCase();
    
    // 判断逻辑：只要是 localhost:3000 就是飞书开发者工具（非真实环境）
    // 其他所有情况都算真实环境
    if (location.includes('localhost:3000')) {
        console.log('检测到 localhost:3000，判断为飞书开发者工具环境');
        return false;
    }
    
    // 其他所有情况都判断为真实飞书环境
    console.log('非 localhost:3000，判断为真实飞书环境');
    return true;
};

/**
 * 检查飞书JSAPI是否已完成鉴权
 * @returns {boolean}
 */
const isJSAPIAuthorized = () => {
    // 检查是否存在已鉴权的SDK对象
    const tt = window.h5sdk?.tt || window.tt;
    const feishu = window.feishu;
    const lark = window.lark;

    // 如果存在这些对象，说明JSAPI已经加载
    // 鉴权状态由UserContext中的handleJSAPIAccess负责
    return !!(tt || feishu || lark);
};

/**
 * 获取飞书SDK对象
 * @returns {object|null}
 */
const getFeishuSDK = () => {
    // 优先级：tt (h5sdk.tt) > feishu > lark
    const tt = window.h5sdk?.tt || window.tt;
    if (tt && typeof tt.showModal === 'function') {
        return { sdk: tt, type: 'tt' };
    }

    const feishu = window.feishu;
    if (feishu && typeof feishu.showModal === 'function') {
        return { sdk: feishu, type: 'feishu' };
    }

    const lark = window.lark;
    if (lark && typeof lark.showModal === 'function') {
        return { sdk: lark, type: 'lark' };
    }

    return null;
};



/**
 * 显示确认对话框（替换 confirm）
 * @param {string} title - 标题
 * @param {string} content - 内容
 * @returns {Promise<boolean>} - 用户是否确认
 */
export const showConfirm = (title = '提示', content = '') => {
    return new Promise(async (resolve) => {
        // 判断是否为真实飞书环境
        const realFeishuEnv = isRealFeishuEnv();

        // 尝试获取飞书SDK对象（依赖UserContext中已完成的鉴权）
        const sdkResult = getFeishuSDK();

        // 调试信息
        console.log('showConfirm - 环境检测:', realFeishuEnv);
        console.log('showConfirm - SDK:', sdkResult ? sdkResult.type : 'null');
        console.log('showConfirm - window.h5sdk:', !!window.h5sdk);

        // 飞书环境 + SDK 可用：用 retryJSAPIOnInvalidSession 包装 SDK 调用，避免鉴权过期导致降级到 confirm 显示 hostname 标题
        if (realFeishuEnv && sdkResult) {
            const sdk = sdkResult.sdk;
            const sdkType = sdkResult.type;

            const callSdkModal = () => new Promise((resolveInner, rejectInner) => {
                if (typeof sdk.showModal === 'function') {
                    sdk.showModal({
                        title: title,
                        content: content,
                        confirmText: '确定',
                        cancelText: '取消',
                        success: (res) => {
                            if (res.confirm) resolveInner(true);
                            else if (res.cancel) resolveInner(false);
                        },
                        fail: (err) => rejectInner(err)
                    });
                    return;
                }
                if (typeof sdk.confirm === 'function') {
                    sdk.confirm({
                        title: title,
                        content: content,
                        confirmText: '确定',
                        cancelText: '取消',
                        success: (res) => resolveInner(res.confirm),
                        fail: (err) => rejectInner(err)
                    });
                    return;
                }
                // SDK 既无 showModal 也无 confirm
                rejectInner(new Error('SDK 无可用模态 API'));
            });

            try {
                const ok = await retryJSAPIOnInvalidSession(callSdkModal);
                resolve(ok);
                return;
            } catch (err) {
                // 重试后仍失败（含 invalid session 重试失败、SDK API 缺失等）：
                // 安全默认视为"用户取消"，避免意外执行后续操作；同时不弹浏览器原生 confirm 防止显示 hostname 标题
                console.error(`${sdkType}.showModal/confirm 调用失败（已重试）:`, err);
                resolve(false);
                return;
            }
        }

        // 非飞书环境或SDK不可用：开发环境使用原生 confirm（仅本地开发 localhost:3000 会走这里）
        const result = window.confirm(`${title}\n${content}`);
        resolve(result);
    });
};

/**
 * 显示错误弹窗（仅"确定"按钮，无取消），用于长错误提示。
 * 与 showToast 不同：飞书原生 toast 会截断长字符串，此函数确保用户完整阅读。
 * @param {string} message - 错误内容
 */
export const showErrorModal = async (message = '') => {
    const realFeishuEnv = isRealFeishuEnv();
    const sdkResult = getFeishuSDK();

    if (realFeishuEnv && sdkResult) {
        const sdk = sdkResult.sdk;
        const sdkType = sdkResult.type;

        // 用 retryJSAPIOnInvalidSession 包装：鉴权过期时自动重新鉴权，避免 showModal 失败降级到 alert（带 hostname 标题）
        const callModal = () => new Promise((resolve, reject) => {
            if (typeof sdk.showModal === 'function') {
                sdk.showModal({
                    title: '提示',
                    content: message,
                    confirmText: '我知道了',
                    showCancel: false,
                    success: () => resolve(true),
                    fail: (err) => reject(err)
                });
            } else if (typeof sdk.confirm === 'function') {
                sdk.confirm({
                    title: '提示',
                    content: message,
                    confirmText: '我知道了',
                    cancelText: '取消',
                    success: (res) => resolve(res.confirm),
                    fail: (err) => reject(err)
                });
            } else {
                reject(new Error('SDK 无 showModal/confirm API'));
            }
        });

        try {
            await retryJSAPIOnInvalidSession(callModal);
            return;
        } catch (err) {
            // 重试后仍失败：仅记录日志，不再用 window.alert 兜底（避免浏览器原生 alert 显示 hostname 标题打断用户体验）
            console.error(`${sdkType} 弹窗调用失败（已重试）:`, err, '原始消息:', message);
        }
    }
};